/**
 * @file 教材の面 — 登録した教材を「使うステップ」ごとに、表紙・状態・単元・ページをまとめて見る。
 *
 * @what: 使うステップ (入園前 → 年長 → 決めていない) の節に分け、節の中は 使っている → 用意した →
 *   検討中 → 使い終えた の順に並べる。カードに表紙 (Amazon の書影)・書名・出版社・状態ボタン・
 *   使うステップ・紐づけた単元 (「単元を選ぶ」で変更)・ページ・固定のメモ・商品ページへのリンク。
 *   日付つきのメモ (履歴) は取り組みの記録 (progress log) をその教材で絞って出し、「メモを足す」は
 *   今日の日付の記録として残す (記録タブと同じもの。教材側に別の履歴を持たない)。
 *   上に状態ごとの件数 (押すと絞り込み) と追加の入口。
 * @why: 教材は 100 冊を超え、見取り図のマスや計画の下に置くと埋もれる。買うものと進めている
 *   ものを 1 か所で見て、そこから買いに行けるようにする (代表 2026-08-24)。
 * @failure: 変更はすべて draft (materials) 経由で、保存バーで PUT される。
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { materialCoverUrl } from "../api/bookCover";
import { PhotoOverlay } from "./PhotoOverlay";
import {
  STUDY_MATERIAL_STATUS_LABELS,
  type StudyMaterialInput,
  type StudyMaterialStatus,
  type StudyProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import type { AttainmentModel } from "../model";
import { MaterialAddForm } from "./MaterialAddForm";
import { MaterialEditor } from "./MaterialEditor";
import "./study.css";

/** 節の中の並び (いま使っているものが先)。 */
const STATUS_ORDER: readonly StudyMaterialStatus[] = [
  "in_use",
  "ready",
  "considering",
  "finished",
  "excluded",
];

type Filter = "all" | StudyMaterialStatus;

export function MaterialsPanel({
  model,
  materials,
  onChange,
  currentStageId,
  logs,
  onAddLog,
  logBusy,
  logError,
}: {
  model: AttainmentModel;
  materials: StudyMaterialInput[];
  onChange: (next: StudyMaterialInput[]) => void;
  /** お子さんの「いまここ」。追加する教材の初期のステップと、節の印に使う。 */
  currentStageId?: string;
  /** 取り組みの記録 (読めているぶん)。教材ごとのメモの履歴に使う。 */
  logs?: StudyProgressLog[];
  /** 「メモを足す」で今日の記録を残す。無ければ履歴だけ出す。 */
  onAddLog?: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  logBusy?: boolean;
  /** 記録の追加に失敗したときの文言 (hook が持つ)。「メモを足す」の下に出す。 */
  logError?: string | null;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  // 追加した直後のカードは、節の末尾にあって画面外になりやすいので、そこまで送って目立たせる
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const justAddedRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!justAddedId) return;
    const el = justAddedRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
    const timer = setTimeout(() => setJustAddedId(null), 2500);
    return () => clearTimeout(timer);
  }, [justAddedId]);
  const add = (m: StudyMaterialInput) => {
    onChange([...materials, m]);
    setFilter("all");
    setJustAddedId(m.id);
  };
  const counts = useMemo(() => {
    const c: Record<StudyMaterialStatus, number> = {
      considering: 0,
      ready: 0,
      in_use: 0,
      finished: 0,
      excluded: 0,
    };
    for (const m of materials) c[m.status] += 1;
    return c;
  }, [materials]);

  const update = (id: string, next: StudyMaterialInput) => {
    onChange(materials.map((m) => (m.id === id ? next : m)));
  };
  const remove = (id: string) => onChange(materials.filter((m) => m.id !== id));

  const sections = [
    ...model.stages.map((s) => ({ id: s.id, title: s.period, sub: s.label })),
    { id: "", title: "使うステップを決めていない", sub: "" },
  ];
  const visible = filter === "all" ? materials : materials.filter((m) => m.status === filter);
  const byStage = (stageId: string) =>
    visible
      .filter((m) => (m.stage_id ?? "") === stageId)
      .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));

  return (
    <section className="study-materials-panel" aria-label="教材">
      <div className="study-material-toolbar">
        <div className="study-material-filters" role="group" aria-label="状態で絞り込む">
          <button
            type="button"
            className={`study-filter-chip${filter === "all" ? " study-filter-chip--on" : ""}`}
            aria-pressed={filter === "all"}
            onClick={() => setFilter("all")}
          >
            すべて <span className="study-filter-count">{materials.length}</span>
          </button>
          {STATUS_ORDER.map((st) => (
            <button
              key={st}
              type="button"
              className={`study-filter-chip study-filter-chip--${st}${
                filter === st ? " study-filter-chip--on" : ""
              }`}
              aria-pressed={filter === st}
              onClick={() => setFilter(filter === st ? "all" : st)}
            >
              {STUDY_MATERIAL_STATUS_LABELS[st]}{" "}
              <span className="study-filter-count">{counts[st]}</span>
            </button>
          ))}
        </div>
        <details className="study-material-add-details" open={materials.length === 0}>
          <summary>教材を追加する</summary>
          <MaterialAddForm stageId={currentStageId ?? model.stages[0]?.id ?? ""} onAdd={add} />
        </details>
      </div>

      {materials.length === 0 ? (
        <p className="study-muted">まだ教材が登録されていません。</p>
      ) : null}

      {sections.map((sec) => {
        const list = byStage(sec.id);
        if (list.length === 0) return null;
        const isCurrent = sec.id !== "" && sec.id === currentStageId;
        // 「単元を選ぶ」に出す単元は、その教材を使うステップのもの (決めていなければ いまここ か最初のステップ)
        const pickerStage =
          model.stages.find((s) => s.id === (sec.id || currentStageId)) ?? model.stages[0];
        if (!pickerStage) return null;
        return (
          <section
            key={sec.id || "none"}
            className={`study-material-section${isCurrent ? " study-material-section--current" : ""}`}
            aria-label={`${sec.title}の教材`}
          >
            <h3 className="study-material-section-title">
              {sec.title}
              {sec.sub ? <span className="study-material-section-sub">{sec.sub}</span> : null}
              {isCurrent ? <span className="study-now-badge">いまここ</span> : null}
              <span className="study-material-section-count">{list.length} 冊</span>
            </h3>
            <ul className="study-material-grid">
              {list.map((m) => (
                <li
                  key={m.id}
                  ref={m.id === justAddedId ? justAddedRef : null}
                  className={`study-material-card study-material-card--${m.status}${
                    m.id === justAddedId ? " study-material-card--new" : ""
                  }`}
                >
                  <MaterialCover isbn={m.isbn} url={m.url} title={m.title} />
                  <div className="study-material-card-main">
                    <div className="study-material-card-head">
                      <span className="study-material-title">{m.title}</span>
                      {m.publisher || m.isbn ? (
                        <span className="study-material-book">
                          {m.publisher ? <span>{m.publisher}</span> : null}
                          {m.isbn ? <span>ISBN {m.isbn}</span> : null}
                        </span>
                      ) : null}
                    </div>
                    <MaterialEditor
                      model={model}
                      material={m}
                      onChange={(next) => update(m.id, next)}
                      onRemove={() => remove(m.id)}
                      pickerStage={pickerStage}
                      logs={(logs ?? []).filter(
                        (l) => l.material_id === m.id && (l.memo || l.to_page),
                      )}
                      {...(onAddLog ? { onAddLog } : {})}
                      logBusy={logBusy === true}
                      logError={logError ?? null}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </section>
  );
}

/**
 * 表紙の左下に、本だなにあることを表す印を出す。
 *
 * @what: 大きさは表紙の幅に対する割合で決める。
 * @why: 計画に出てくる本は、どこでも同じ印で持っているかどうかが分かるようにする。表紙は場所に
 *   よって 26px から 130px まで幅が違うので、固定の大きさだと小さい表紙では表紙を覆ってしまう
 *   (代表の要望、2026-09-08: 本に対して一定の比率にする。よく見えなくてよい)。
 */
function ShelfMark() {
  return (
    <span className="study-shelf-mark" title="本だなにあります">
      <span className="study-visually-hidden">本だなにあります</span>
      <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
        <path d="M2.5 6.3 4.7 8.5 9.5 3.7" />
      </svg>
    </span>
  );
}

/**
 * 表紙。Amazon の書影は無いとき 1×1 の画像が返るので、読み込み後の大きさで枠に戻す。
 *
 * @what: enlargeable を渡すと、タップで全画面の拡大が開く (候補の一覧のように表紙が小さく、
 *   表紙で本を見分けたい場所で使う)。書影が引けない本は頭文字の枠のままで、拡大は付かない。
 *   onShelf を渡すと、本だなにあることを表す印を左下に重ねる (書影の有無によらず出す)。
 * @why: よむの候補は1段階に50件並び、表紙が2.6rem では絵柄が分からない (代表、2026-08-28)。
 *   印を表紙の側に持たせるのは、計画のどの場所でも同じ見え方にするためである (代表、2026-09-08)。
 */
export function MaterialCover({
  isbn,
  url,
  title,
  enlargeable = false,
  onShelf = false,
}: {
  isbn: string | undefined;
  /** 商品ページ URL。ISBN が無い教材 (七田式プリント等) は Amazon の ASIN から書影を引く。 */
  url?: string | undefined;
  title: string;
  /** タップで拡大できるようにする (既定は拡大なし)。 */
  enlargeable?: boolean;
  /** 本だなにこの本があることを表す印を左下に出す (既定は出さない)。 */
  onShelf?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const src = materialCoverUrl({ ...(isbn ? { isbn } : {}), ...(url ? { url } : {}) });
  // 印は表紙に重ねるので、印を出すときだけ位置の基準になる入れ物で包む
  const withMark = (cover: ReactNode) =>
    onShelf ? (
      <span className="study-cover-wrap">
        {cover}
        <ShelfMark />
      </span>
    ) : (
      cover
    );
  if (!src || failed) {
    return withMark(
      <div className="study-material-cover study-material-cover--empty" aria-hidden="true">
        <span>{title.slice(0, 2)}</span>
      </div>,
    );
  }
  const image = (
    <img
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      onLoad={(e) => {
        if (e.currentTarget.naturalWidth <= 1) setFailed(true);
      }}
    />
  );
  if (!enlargeable) {
    return withMark(<div className="study-material-cover">{image}</div>);
  }
  return (
    <>
      {withMark(
        <button
          type="button"
          className="study-material-cover study-material-cover--btn"
          aria-label={`${title} の表紙を拡大する`}
          title="タップで拡大"
          onClick={() => setOpen(true)}
        >
          {image}
        </button>,
      )}
      {open ? (
        <PhotoOverlay
          src={src}
          alt={`${title} の表紙`}
          label={`${title} の表紙`}
          referrerPolicy="no-referrer"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
